<template>
  <section class="award-ranking-section">
    <award-ranking-header-section :title="title" :description="description" />

    <award-ranking-sponsor-parts
      :targetYear="targetYear"
      :category="category"
      :key="sponsorPartsRenderKey"
    />

    <template v-if="rankings && rankings.length > 0">
      <award-winner-section
        v-for="(winner, idx) in partitionedRankingData.winner"
        :key="`award-winner${idx}`"
        :category="category"
        :badgeImage="badgeImage"
        :winnerData="winner"
      />

      <award-ranking-list-section
        v-if="partitionedRankingData.rankingOtherFirst.length > 0"
        :category="category"
        :rankingDataOtherFirst="partitionedRankingData.rankingOtherFirst"
      />
    </template>
    <template v-else>
      <p class="award-ranking-section__no-data" v-html="$tc('LinkPage.AwardPage.noData')"></p>
    </template>
  </section>
</template>

<script lang="ts">
import { computed, ref, defineComponent, PropType, watch } from '@vue/composition-api'
import { partition } from 'lodash'
import { AwardRankingData } from '@/components/LinkPage/AwardPane.vue'
import AwardRankingHeaderSection from '@/components/LinkPage/AwardPane/AwardRankingHeaderSection.vue'
import AwardWinnerSection from '@/components/LinkPage/AwardPane/AwardWinnerSection.vue'
import AwardRankingListSection from '@/components/LinkPage/AwardPane/AwardRankingListSection.vue'
import { AwardRankingCategoryType } from '@/store/stores/collectionModule/documents/award/AwardDataDocument'
import AwardRankingSponsorParts from '@/components/LinkPage/AwardPane/parts/AwardRankingSponsorParts.vue'

/**
 * Award Ranking セクション
 */
export default defineComponent({
  name: 'AwardRankingSection',
  components: {
    AwardRankingSponsorParts,
    AwardRankingHeaderSection,
    AwardWinnerSection,
    AwardRankingListSection,
  },
  props: {
    /**
     * 対象年
     */
    targetYear: {
      type: Number,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    description: {
      type: String,
      required: true,
    },
    badgeImage: {
      type: String,
      required: true,
    },
    category: {
      type: String as PropType<AwardRankingCategoryType>,
      default: 'RECOMMEND_DRIVER',
    },
    rankings: {
      type: Array as PropType<AwardRankingData[]>,
    },
  },
  setup(props) {
    const sponsorPartsRenderKey = ref(0)

    /** 1位と1位以外でランキングデータを分ける */
    const partitionedRankingData = computed(() => {
      const partitionedData = partition(props.rankings, (r) => r.ranking === 1)
      return {
        winner: partitionedData[0] as Array<AwardRankingData>,
        rankingOtherFirst: partitionedData[1] as Array<AwardRankingData>,
      }
    })

    watch(
      () => props.targetYear,
      () => {
        sponsorPartsRenderKey.value += 1
      },
    )

    return {
      sponsorPartsRenderKey,
      partitionedRankingData,
    }
  },
})
</script>

<style lang="scss" scoped>
.award-ranking-section {
  width: 100%;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.4);
  backdrop-filter: blur(6px);
  color: $white;
  overflow: hidden;

  &__no-data {
    background: rgba(255, 255, 255, 0.3);
    padding: 8px 0 22px;
    font-family: $fontSignup;
    font-weight: 500;
    font-size: 12px;
    line-height: 1.67;
    text-align: center;
    letter-spacing: 0.02em;
    font-feature-settings: 'palt' on;
  }
}
</style>
